<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Avatar from 'next/avatar/Avatar.vue';
import Spinner from 'shared/components/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  ticketCount: { type: Number, required: true },
});

const emit = defineEmits(['select', 'close']);

const query = ref('');
const selectedAgent = ref(null);
const goBackToAgentList = ref(false);

const agents = useMapGetter('agents/getAgents');
const uiFlags = useMapGetter('bulkActions/getUIFlags');

const noneOption = { id: null, name: 'None', confirmed: true };

const assignableAgents = computed(() => {
  const list = agents.value || [];
  return list.filter(a => a.confirmed);
});

const filteredAgents = computed(() => {
  if (query.value) {
    const q = query.value.toLowerCase();
    return [
      noneOption,
      ...assignableAgents.value.filter(a =>
        a.name.toLowerCase().includes(q)
      ),
    ];
  }
  return [noneOption, ...assignableAgents.value];
});

const ticketLabel = computed(
  () => (props.ticketCount > 1 ? 'tickets' : 'ticket')
);

const submit = () => {
  emit('select', selectedAgent.value);
};

const goBack = () => {
  goBackToAgentList.value = true;
  selectedAgent.value = null;
};

const assignAgent = agent => {
  selectedAgent.value = agent;
};

const onClose = () => {
  emit('close');
};

const onCloseAgentList = () => {
  if (selectedAgent.value === null && !goBackToAgentList.value) {
    onClose();
  }
  goBackToAgentList.value = false;
};

const agentKey = agent => (agent.id ?? 'none');

onMounted(() => {
  if (!agents.value?.length) {
    // Agents may already be loaded from TicketListPanel/TicketDetails
    // If not, the parent or a global loader should have fetched them
  }
});
</script>

<template>
  <div v-on-clickaway="onCloseAgentList" class="bulk-action__agents">
    <div class="triangle">
      <svg height="12" viewBox="0 0 24 12" width="24">
        <path d="M20 12l-8-8-12 12" fill-rule="evenodd" stroke-width="1px" />
      </svg>
    </div>
    <div class="flex items-center justify-between header">
      <span>{{ $t('BULK_ACTION.AGENT_SELECT_LABEL') }}</span>
      <NextButton ghost xs slate icon="i-lucide-x" @click="onClose" />
    </div>
    <div class="container">
      <div class="agent__list-container">
        <ul v-if="!selectedAgent">
          <li class="search-container">
            <div
              class="flex items-center justify-between h-8 gap-2 agent-list-search"
            >
              <fluent-icon icon="search" class="search-icon" size="16" />
              <input
                v-model="query"
                type="search"
                :placeholder="$t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
                class="reset-base !outline-0 !text-sm agent--search_input"
              />
            </div>
          </li>
          <li v-for="agent in filteredAgents" :key="agentKey(agent)">
            <div class="agent-list-item" @click="assignAgent(agent)">
              <Avatar
                :name="agent.name"
                :src="agent.thumbnail"
                :status="agent.availability_status"
                :size="22"
                hide-offline-status
                rounded-full
              />
              <span class="my-0 text-n-slate-12">
                {{ agent.name }}
              </span>
            </div>
          </li>
        </ul>
        <div v-else class="agent-confirmation-container">
          <p v-if="selectedAgent.id">
            {{
              $t('BULK_ACTION.ASSIGN_AGENT_CONFIRMATION_TICKETS', {
                ticketCount,
                ticketLabel,
              })
            }}
            <strong>{{ selectedAgent.name }}</strong>
            <span>?</span>
          </p>
          <p v-else>
            {{
              $t('BULK_ACTION.UNASSIGN_AGENT_CONFIRMATION_TICKETS', {
                ticketCount,
                ticketLabel,
              })
            }}
          </p>
          <div class="agent-confirmation-actions">
            <NextButton
              faded
              sm
              slate
              type="reset"
              :label="$t('BULK_ACTION.GO_BACK_LABEL')"
              @click="goBack"
            />
            <NextButton
              sm
              type="submit"
              :label="$t('BULK_ACTION.YES')"
              :is-loading="uiFlags?.isUpdating"
              @click="submit"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bulk-action__agents {
  @apply max-w-[75%] absolute ltr:right-2 rtl:left-2 top-12 origin-top-right w-auto z-20 min-w-[15rem] bg-n-alpha-3 backdrop-blur-[100px] border-n-weak rounded-lg border border-solid shadow-md;
  .header {
    @apply p-2.5;

    span {
      @apply text-sm font-medium;
    }
  }

  .container {
    @apply overflow-y-auto max-h-[15rem];
    .agent__list-container {
      @apply h-full;
    }
    .agent-list-search {
      @apply py-0 px-2.5 bg-n-alpha-black2 border border-solid border-n-strong rounded-md;
      .search-icon {
        @apply text-n-slate-10;
      }

      .agent--search_input {
        @apply border-0 text-xs m-0 dark:bg-transparent bg-transparent h-[unset] w-full;
      }
    }
  }
  .triangle {
    @apply block z-10 absolute -top-3 text-left ltr:right-[--triangle-position] rtl:left-[--triangle-position];

    svg path {
      @apply fill-n-alpha-3 backdrop-blur-[100px] stroke-n-weak;
    }
  }
}
ul {
  @apply m-0 list-none;

  li {
    &:last-child {
      .agent-list-item {
        @apply last:rounded-b-lg;
      }
    }
  }
}

.agent-list-item {
  @apply flex items-center p-2.5 gap-2 cursor-pointer hover:bg-n-slate-3 dark:hover:bg-n-solid-3;
  span {
    @apply text-sm;
  }
}

.agent-confirmation-container {
  @apply flex flex-col h-full p-2.5;
  p {
    @apply flex-grow;
  }
  .agent-confirmation-actions {
    @apply w-full grid grid-cols-2 gap-2.5;
  }
}
.search-container {
  @apply py-0 px-2.5 sticky top-0 z-20 bg-n-alpha-3 backdrop-blur-[100px];
}
</style>
